import React, { useState } from 'react';
import { useOrg } from '../../contexts/OrgContext';
import { referAndEarnAPI } from '../../services/api';
import { Card, Button, Spinner } from '../../components/UI';
import Layout from '../../components/Layout/Layout';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  HiOutlineGift,
  HiOutlineClipboardCopy,
  HiOutlineLink,
  HiOutlineCurrencyRupee,
  HiOutlineShare,
  HiOutlineUserAdd,
  HiOutlineCreditCard,
  HiOutlineShoppingCart,
  HiOutlineMail,
} from 'react-icons/hi';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const ReferAndEarn = () => {
  const { organization, refreshOrg } = useOrg();
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setInviteSending(true);
    try {
      const res = await referAndEarnAPI.sendInvite(inviteEmail.trim());
      toast.success(res.data?.message || 'Invite sent');
      setInviteEmail('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invite');
    } finally {
      setInviteSending(false);
    }
  };

  const referAndEarnConfig = organization?.referAndEarn || {};
  const coinValueInRupees = referAndEarnConfig.coinValueInRupees || 1;
  const coinBalance = organization?.coinBalance || 0;
  const referralToken = organization?.referralToken || '';
  const referralLink = organization?.linkHash ? `${window.location.origin}/r/${organization.linkHash}` : '';

  // Reward terms are fetched live from superadmin (PlatformConfig → Refer & Earn
  // settings) via organization.referAndEarn — nothing here is hardcoded.
  const rewardLabel = referAndEarnConfig.rewardType === 'percentage'
    ? `${referAndEarnConfig.rewardValue}% of their plan, in coins`
    : `${inr(referAndEarnConfig.rewardValue)} in coins`;
  const rewardShort = referAndEarnConfig.rewardType === 'percentage'
    ? `${referAndEarnConfig.rewardValue || 0}%`
    : inr(referAndEarnConfig.rewardValue);

  const steps = [
    {
      icon: HiOutlineShare,
      title: 'Share your link',
      description: 'Send your unique referral link or code to a friend or business you know.',
    },
    {
      icon: HiOutlineUserAdd,
      title: 'They sign up',
      description: 'They register a new organization and enter your code during signup.',
    },
    {
      icon: HiOutlineCreditCard,
      title: 'They pay for a plan',
      description: 'Once they complete their first paid-plan payment, your reward is triggered.',
    },
    {
      icon: HiOutlineGift,
      title: 'You earn coins',
      description: `You're credited ${rewardLabel} — automatically, no action needed.`,
    },
    {
      icon: HiOutlineShoppingCart,
      title: 'Spend your coins',
      description: 'Use your coin balance as a discount the next time you upgrade or renew your own plan.',
    },
  ];

  React.useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      setHistoryLoading(true);
      try {
        const res = await referAndEarnAPI.getCoinTransactions({ limit: 50 });
        if (!cancelled) setHistory(res.data?.data || []);
      } catch (err) {
        console.error('Failed to load coin transaction history:', err);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    loadHistory();
    return () => { cancelled = true; };
  }, []);

  const handleGenerateLink = async () => {
    setGenerating(true);
    try {
      await referAndEarnAPI.getOrGenerateToken();
      await refreshOrg();
      toast.success('Your referral link is ready!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate referral link');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Failed to copy — please copy it manually');
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <Layout>
    <div className="max-w-6xl mx-auto space-y-4 lg:space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 via-primary-500 to-indigo-600 p-6 sm:p-8 text-white shadow-lg">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute -right-2 bottom-0 w-24 h-24 rounded-full bg-white/10" />
        <div className="relative flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <HiOutlineGift className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Refer &amp; Earn</h1>
            <p className="text-sm sm:text-base text-white/90 mt-1 max-w-xl">
              Invite other businesses to TeleValley. For every one that signs up and pays for a
              plan through your link, you earn <strong>{rewardShort}</strong> in coins —
              redeemable toward your own plan.
            </p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <Card>
        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-4">How It Works</p>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 sm:gap-3">
          {steps.map((step, idx) => (
            <div key={step.title} className="flex sm:flex-col items-start sm:items-center gap-3 sm:gap-2 sm:text-center">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 relative">
                <step.icon className="w-5 h-5 text-primary-600" />
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary-600 text-white text-[9px] font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold text-secondary-900">{step.title}</p>
                <p className="text-xs text-secondary-500 mt-0.5 sm:leading-snug">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Coin balance */}
      <Card className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider">Your Coin Balance</p>
          <p className="text-3xl font-bold text-secondary-900 mt-1">{coinBalance.toLocaleString('en-IN')} coins</p>
          <p className="text-sm text-secondary-500 mt-1">≈ {inr(coinBalance * coinValueInRupees)} · 1 coin = {inr(coinValueInRupees)}</p>
        </div>
        <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center">
          <HiOutlineCurrencyRupee className="w-7 h-7 text-primary-600" />
        </div>
      </Card>

      {/* Referral link */}
      <Card>
        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">Your Referral Link</p>
        {!referralToken ? (
          <Button variant="primary" onClick={handleGenerateLink} loading={generating} disabled={generating} icon={<HiOutlineLink className="w-4 h-4" />}>
            Get my referral link
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                readOnly
                value={referralLink}
                className="flex-1 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-700"
              />
              <Button variant="outline" onClick={() => copyToClipboard(referralLink, 'Referral link')} icon={<HiOutlineClipboardCopy className="w-4 h-4" />}>
                Copy Link
              </Button>
            </div>
            <div className="flex items-center gap-2 text-sm text-secondary-500">
              <span>Or share just the code:</span>
              <button
                type="button"
                onClick={() => copyToClipboard(referralToken, 'Referral code')}
                className="font-mono font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded hover:bg-primary-100"
              >
                {referralToken}
              </button>
            </div>
          </div>
        )}
        <p className="text-xs text-secondary-400 mt-3">
          Coins are automatically offered as a discount the next time you <Link to="/app/checkout" className="text-primary-600 hover:underline">upgrade or renew your plan</Link>.
        </p>
      </Card>

      {/* Invite via email */}
      {referralToken && (
        <Card>
          <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">Invite via Email</p>
          <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="business@company.com"
              disabled={inviteSending}
              className="flex-1 rounded-lg border border-secondary-300 bg-white px-3 py-2.5 text-sm shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-secondary-50"
            />
            <Button type="submit" variant="primary" loading={inviteSending} disabled={inviteSending} icon={<HiOutlineMail className="w-4 h-4" />}>
              Send Invite
            </Button>
          </form>
          <p className="text-xs text-secondary-400 mt-3">
            We'll email them your referral link directly — no need to copy and paste it yourself.
          </p>
        </Card>
      )}

      {/* Coin transaction history */}
      <Card>
        <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wider mb-3">Coin History</p>
        {historyLoading ? (
          <div className="flex items-center justify-center py-8"><Spinner /></div>
        ) : !history || history.length === 0 ? (
          <p className="text-sm text-secondary-500 py-4">No coin activity yet — share your referral link to start earning.</p>
        ) : (
          <div className="divide-y divide-secondary-100">
            {history.map((tx) => (
              <div key={tx._id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-secondary-900">
                    {tx.reason === 'referral_reward'
                      ? `Referred ${tx.referredOrganizationId?.name || 'an organization'}`
                      : 'Redeemed at checkout'}
                  </p>
                  <p className="text-xs text-secondary-400">{formatDate(tx.createdAt)}</p>
                </div>
                <span className={`text-sm font-bold ${tx.type === 'earned' ? 'text-success-600' : 'text-secondary-500'}`}>
                  {tx.type === 'earned' ? '+' : '-'}{tx.amount} coins
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
    </Layout>
  );
};

export default ReferAndEarn;
